import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const QUESTION_TYPES = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'select', label: 'Dropdown (Single)' },
    { value: 'radio', label: 'Radio Buttons (Single)' },
    { value: 'checkbox', label: 'Checkboxes (Multiple)' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Number' },
    { value: 'link_button', label: 'Link Button (External URL)' },
    { value: 'section', label: 'Section Heading (Display Only)' },
];

const emptyQuestion = {
    id: '', label: '', type: 'text', options: [], required: false, step: 1, placeholder: '', maxSelections: null,
    allowSkip: false, linkUrl: '', linkLabel: '', linkStyle: 'primary'
};

const generateId = () => `q_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

const SurveyBuilder = ({ surveyId, onBack, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [survey, setSurvey] = useState({
        title: '', slug: '', description: '', welcomeMessage: '', thankYouMessage: '',
        bannerImage: '', status: 'draft', totalSteps: 3, requireLogin: false, allowMultiple: false,
        startDate: '', endDate: '', questions: []
    });
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [errors, setErrors] = useState({});

    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        if (surveyId && surveyId !== 'new') fetchSurvey();
    }, [surveyId]);

    const fetchSurvey = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/survey/manage/${surveyId}`, { headers });
            if (res.data.success) {
                const s = res.data.data.survey;
                setSurvey({
                    title: s.title || '', slug: s.slug || '', description: s.description || '',
                    welcomeMessage: s.welcome_message || '', thankYouMessage: s.thank_you_message || '',
                    bannerImage: s.banner_image || '', status: s.status || 'draft',
                    totalSteps: s.total_steps || 3, requireLogin: !!s.require_login,
                    allowMultiple: !!s.allow_multiple,
                    startDate: s.start_date ? s.start_date.slice(0, 16) : '',
                    endDate: s.end_date ? s.end_date.slice(0, 16) : '',
                    questions: Array.isArray(s.questions) ? s.questions : []
                });
            }
        } catch (err) { toast.error('Failed to load survey'); }
        finally { setLoading(false); }
    };

    const handleChange = (field) => (e) => {
        const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setSurvey(prev => ({ ...prev, [field]: val }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setSurvey(prev => ({
            ...prev, title,
            slug: !surveyId || surveyId === 'new' ? autoSlug(title) : prev.slug
        }));
    };

    // ===== Question Management =====
    const addQuestion = () => {
        const q = { ...emptyQuestion, id: generateId(), step: 1 };
        setSurvey(prev => ({ ...prev, questions: [...prev.questions, q] }));
        setEditingQuestion(q);
    };

    const updateQuestion = (updatedQ) => {
        setSurvey(prev => ({
            ...prev,
            questions: prev.questions.map(q => q.id === updatedQ.id ? updatedQ : q)
        }));
        setEditingQuestion(updatedQ);
    };

    const removeQuestion = (qId) => {
        setSurvey(prev => ({ ...prev, questions: prev.questions.filter(q => q.id !== qId) }));
        if (editingQuestion?.id === qId) setEditingQuestion(null);
    };

    const moveQuestion = (index, direction) => {
        const newQuestions = [...survey.questions];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newQuestions.length) return;
        [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
        setSurvey(prev => ({ ...prev, questions: newQuestions }));
    };

    const duplicateQuestion = (q) => {
        const dup = { ...q, id: generateId(), label: `${q.label} (Copy)` };
        setSurvey(prev => ({ ...prev, questions: [...prev.questions, dup] }));
    };

    // ===== Save Survey =====
    const validate = () => {
        const errs = {};
        if (!survey.title.trim()) errs.title = 'Title is required';
        if (!survey.slug.trim()) errs.slug = 'Slug is required';
        if (survey.questions.length === 0) errs.questions = 'Add at least one question';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = async (statusOverride) => {
        if (!validate()) return;

        try {
            setSaving(true);
            const payload = { ...survey, status: statusOverride || survey.status };

            if (surveyId && surveyId !== 'new') {
                await axios.put(`${API_URL}/survey/manage/${surveyId}`, payload, { headers });
                toast.success('Survey updated!');
            } else {
                const res = await axios.post(`${API_URL}/survey/manage`, payload, { headers });
                toast.success('Survey created!');
                if (onSaved) onSaved(res.data.data.id);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save survey');
        } finally { setSaving(false); }
    };

    if (loading) {
        return <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>;
    }

    return (
        <div>
            <div className="d-flex align-items-center mb-4">
                <button className="btn btn-outline-secondary btn-sm me-3" onClick={onBack}>
                    <i className="bi bi-arrow-left"></i> Back
                </button>
                <h4 className="mb-0 fw-bold">{surveyId && surveyId !== 'new' ? 'Edit Survey' : 'Create New Survey'}</h4>
            </div>

            <div className="row g-4">
                {/* Left: Survey Settings */}
                <div className="col-lg-5">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                            <h5 className="fw-bold mb-3"><i className="bi bi-gear me-2" style={{ color: '#6B8E23' }}></i>Survey Settings</h5>

                            <div className="mb-3">
                                <label className="form-label fw-semibold">Title <span className="text-danger">*</span></label>
                                <input type="text" className={`form-control ${errors.title ? 'is-invalid' : ''}`} value={survey.title} onChange={handleTitleChange} />
                                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-semibold">URL Slug <span className="text-danger">*</span></label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text">/survey/</span>
                                    <input type="text" className={`form-control ${errors.slug ? 'is-invalid' : ''}`} value={survey.slug} onChange={handleChange('slug')} />
                                </div>
                                {errors.slug && <div className="invalid-feedback d-block">{errors.slug}</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-semibold">Description</label>
                                <textarea className="form-control" rows={2} value={survey.description} onChange={handleChange('description')} />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-semibold">Welcome Message</label>
                                <textarea className="form-control" rows={3} value={survey.welcomeMessage} onChange={handleChange('welcomeMessage')} placeholder="Shown at the top of the survey..." />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-semibold">Thank You Message</label>
                                <textarea className="form-control" rows={2} value={survey.thankYouMessage} onChange={handleChange('thankYouMessage')} placeholder="Shown after submission..." />
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-6">
                                    <label className="form-label fw-semibold">Number of Steps</label>
                                    <input type="number" className="form-control" min={1} max={10} value={survey.totalSteps}
                                        onChange={e => setSurvey(prev => ({ ...prev, totalSteps: parseInt(e.target.value) || 3 }))} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label fw-semibold">Status</label>
                                    <select className="form-select" value={survey.status} onChange={handleChange('status')}>
                                        <option value="draft">Draft</option>
                                        <option value="published">Published</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>
                            </div>

                            <div className="row g-3 mb-3">
                                <div className="col-6">
                                    <label className="form-label fw-semibold">Start Date</label>
                                    <input type="datetime-local" className="form-control" value={survey.startDate} onChange={handleChange('startDate')} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label fw-semibold">End Date</label>
                                    <input type="datetime-local" className="form-control" value={survey.endDate} onChange={handleChange('endDate')} />
                                </div>
                            </div>

                            <div className="form-check mb-2">
                                <input className="form-check-input" type="checkbox" checked={survey.requireLogin} onChange={handleChange('requireLogin')} id="reqLogin" />
                                <label className="form-check-label" htmlFor="reqLogin">Require login to submit</label>
                            </div>
                            <div className="form-check mb-3">
                                <input className="form-check-input" type="checkbox" checked={survey.allowMultiple} onChange={handleChange('allowMultiple')} id="allowMulti" />
                                <label className="form-check-label" htmlFor="allowMulti">Allow multiple submissions per person</label>
                            </div>

                            <div className="d-flex gap-2">
                                <button className="btn btn-outline-secondary flex-grow-1" onClick={() => handleSave('draft')} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save as Draft'}
                                </button>
                                <button className="btn btn-primary flex-grow-1" onClick={() => handleSave()} disabled={saving}
                                    style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                                    {saving ? 'Saving...' : surveyId && surveyId !== 'new' ? 'Update Survey' : 'Create Survey'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: Questions */}
                <div className="col-lg-7">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="fw-bold mb-0"><i className="bi bi-list-check me-2" style={{ color: '#6B8E23' }}></i>Questions ({survey.questions.length})</h5>
                                <button className="btn btn-sm btn-primary" onClick={addQuestion} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                                    <i className="bi bi-plus-lg me-1"></i>Add Question
                                </button>
                            </div>
                            {errors.questions && <div className="alert alert-danger small">{errors.questions}</div>}

                            {survey.questions.length === 0 && (
                                <div className="text-center py-4 text-muted">
                                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                                    No questions yet. Click "Add Question" to get started.
                                </div>
                            )}

                            {/* Question List */}
                            {survey.questions.map((q, idx) => (
                                <div key={q.id} className={`border rounded p-3 mb-2 ${editingQuestion?.id === q.id ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                                    onClick={() => setEditingQuestion(q)}>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <span className="badge bg-secondary me-2">Step {q.step}</span>
                                            <span className="badge bg-light text-dark me-2">{QUESTION_TYPES.find(t => t.value === q.type)?.label || q.type}</span>
                                            {q.required && <span className="badge bg-danger me-2">Required</span>}
                                            <span className="small fw-medium">{q.label || 'Untitled Question'}</span>
                                        </div>
                                        <div className="d-flex gap-1" onClick={e => e.stopPropagation()}>
                                            <button className="btn btn-sm btn-outline-secondary p-0 px-1" onClick={() => moveQuestion(idx, -1)} title="Move up"><i className="bi bi-chevron-up small"></i></button>
                                            <button className="btn btn-sm btn-outline-secondary p-0 px-1" onClick={() => moveQuestion(idx, 1)} title="Move down"><i className="bi bi-chevron-down small"></i></button>
                                            <button className="btn btn-sm btn-outline-secondary p-0 px-1" onClick={() => duplicateQuestion(q)} title="Duplicate"><i className="bi bi-copy small"></i></button>
                                            <button className="btn btn-sm btn-outline-danger p-0 px-1" onClick={() => removeQuestion(q.id)} title="Delete"><i className="bi bi-trash small"></i></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Question Editor */}
                    {editingQuestion && (
                        <div className="card border-0 shadow-sm mt-3">
                            <div className="card-body p-4">
                                <h5 className="fw-bold mb-3"><i className="bi bi-pencil-square me-2" style={{ color: '#6B8E23' }}></i>Edit Question</h5>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Question Label</label>
                                    <input type="text" className="form-control" value={editingQuestion.label}
                                        onChange={e => updateQuestion({ ...editingQuestion, label: e.target.value })} placeholder="e.g. What is your profession?" />
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Question Type</label>
                                        <select className="form-select" value={editingQuestion.type}
                                            onChange={e => updateQuestion({ ...editingQuestion, type: e.target.value })}>
                                            {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold">Step</label>
                                        <input type="number" className="form-control" min={1} max={survey.totalSteps}
                                            value={editingQuestion.step}
                                            onChange={e => updateQuestion({ ...editingQuestion, step: parseInt(e.target.value) || 1 })} />
                                    </div>
                                    <div className="col-md-3 d-flex align-items-end">
                                        <div className="form-check mb-2">
                                            <input className="form-check-input" type="checkbox" checked={editingQuestion.required}
                                                onChange={e => updateQuestion({ ...editingQuestion, required: e.target.checked })} id="qReq" />
                                            <label className="form-check-label fw-semibold" htmlFor="qReq">Required</label>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Placeholder</label>
                                    <input type="text" className="form-control" value={editingQuestion.placeholder || ''}
                                        onChange={e => updateQuestion({ ...editingQuestion, placeholder: e.target.value })} placeholder="Hint text..." />
                                </div>

                                {/* Options for select/radio/checkbox */}
                                {['select', 'radio', 'checkbox'].includes(editingQuestion.type) && (
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Options</label>
                                        {(editingQuestion.options || []).map((opt, optIdx) => (
                                            <div key={optIdx} className="input-group input-group-sm mb-1">
                                                <input type="text" className="form-control" value={opt}
                                                    onChange={e => {
                                                        const newOpts = [...(editingQuestion.options || [])];
                                                        newOpts[optIdx] = e.target.value;
                                                        updateQuestion({ ...editingQuestion, options: newOpts });
                                                    }}
                                                    placeholder={`Option ${optIdx + 1}`} />
                                                <button className="btn btn-outline-danger" type="button"
                                                    onClick={() => {
                                                        const newOpts = (editingQuestion.options || []).filter((_, i) => i !== optIdx);
                                                        updateQuestion({ ...editingQuestion, options: newOpts });
                                                    }}>
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            </div>
                                        ))}
                                        <button className="btn btn-sm btn-outline-success mt-1" type="button"
                                            onClick={() => updateQuestion({ ...editingQuestion, options: [...(editingQuestion.options || []), ''] })}>
                                            <i className="bi bi-plus-lg me-1"></i>Add Option
                                        </button>
                                        <div className="mt-2">
                                            <small className="text-muted">Or paste multiple options (one per line):</small>
                                            <textarea className="form-control form-control-sm font-monospace mt-1" rows={3}
                                                placeholder={"Option 1\nOption 2\nOption 3"}
                                                onBlur={e => {
                                                    const lines = e.target.value.split('\n').filter(o => o.trim());
                                                    if (lines.length > 0) updateQuestion({ ...editingQuestion, options: lines });
                                                    e.target.value = '';
                                                }} />
                                        </div>
                                        {editingQuestion.type === 'checkbox' && (
                                            <div className="mt-2">
                                                <label className="form-label small fw-semibold">Max Selections (optional)</label>
                                                <input type="number" className="form-control form-control-sm" style={{ width: 100 }}
                                                    min={1} value={editingQuestion.maxSelections || ''}
                                                    onChange={e => updateQuestion({ ...editingQuestion, maxSelections: e.target.value ? parseInt(e.target.value) : null })}
                                                    placeholder="No limit" />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Link Button fields */}
                                {editingQuestion.type === 'link_button' && (
                                    <div className="mb-3">
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">Button Label</label>
                                            <input type="text" className="form-control" value={editingQuestion.linkLabel || ''}
                                                onChange={e => updateQuestion({ ...editingQuestion, linkLabel: e.target.value })}
                                                placeholder="e.g. Pay for Certificate" />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label fw-semibold">Link URL</label>
                                            <input type="url" className="form-control" value={editingQuestion.linkUrl || ''}
                                                onChange={e => updateQuestion({ ...editingQuestion, linkUrl: e.target.value })}
                                                placeholder="https://paystack.shop/pay/..." />
                                        </div>
                                        <div>
                                            <label className="form-label fw-semibold">Button Style</label>
                                            <select className="form-select form-select-sm" value={editingQuestion.linkStyle || 'primary'}
                                                onChange={e => updateQuestion({ ...editingQuestion, linkStyle: e.target.value })}>
                                                <option value="primary">Green (Primary)</option>
                                                <option value="outline">Outline</option>
                                                <option value="danger">Red (Danger)</option>
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Allow Skip checkbox for all input types */}
                                {!['section', 'link_button'].includes(editingQuestion.type) && (
                                    <div className="form-check mb-3">
                                        <input className="form-check-input" type="checkbox" checked={editingQuestion.allowSkip || false}
                                            onChange={e => updateQuestion({ ...editingQuestion, allowSkip: e.target.checked })} id="qAllowSkip" />
                                        <label className="form-check-label" htmlFor="qAllowSkip">Allow user to skip this question</label>
                                    </div>
                                )}

                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditingQuestion(null)}>
                                    <i className="bi bi-check-lg me-1"></i>Done Editing
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SurveyBuilder;
